import { Info } from 'lucide-react';
import { cn } from '~/utils';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  dataTestId?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  dataTestId,
}: Readonly<SwitchProps>) {
  return (
    <label className="flex items-center gap-2 text-sm text-neut-prim cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-describedby={hint ? `${dataTestId ?? 'switch'}-hint` : undefined}
        disabled={disabled}
        data-testid={dataTestId}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-prim' : 'bg-neut-300',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-neut-0 transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5'
          )}
        />
      </button>
      <span className="flex items-center gap-1">
        {label}
        {hint && (
          <span
            id={`${dataTestId ?? 'switch'}-hint`}
            title={hint}
            aria-label={hint}
            className="text-neut-400"
          >
            <Info size={14} aria-hidden />
          </span>
        )}
      </span>
    </label>
  );
}
